import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { QueryClient } from "react-query";
import { getPlatform } from "@shared/utils/get-platform";
import type {
  Identifier,
  UrlInjectionMap,
  UrlRouteMap,
  PaginatorContract,
} from "../types";
import Url from "../utils/Url";
import { IResponseHandler, ResponseHandler } from "./ResponseHandler";
import { getBaseUrl } from "../config/base-url";
import HttpRequestFactory from "../factories/HttpRequestFactory";

/**
 * This class is used to represent a repository of models
 * that can be accessed via the API.
 * @see ../README.md
 */
export default abstract class Repository<T> implements RepositoryContract<T> {
  /**
   * Handler for API responses, used to process and handle success and error responses uniformly
   */
  private responseHandler: IResponseHandler;

  /**
   * The endpoint to hit per each repository
   */
  public abstract readonly endpoint: string;

  /**
   * The primary key in the database that represents a model
   */
  public readonly primaryKey: string = "id";

  // Add a property to hold the class name
  public abstract readonly repositoryName: string;

  /**
   * Publicly facing url map which can be extended to include
   * more endpoints. If you need to add additional routes add
   * them here instead of overriding the urlMap private prop.
   *
   * @example  return { ...this.#urlMap, customRoute: 'custom/{val?}/route' }
   */
  public get urlMap(): UrlRouteMap {
    return this.defaultUrlMap;
  }

  /**
   * A map showing each endpoint url and their routes
   */
  protected readonly defaultUrlMap: UrlRouteMap = {
    list: "/",
    paginate: "/",
    create: "/",
    retrieve: `{${this.primaryKey}}`,
    update: `{${this.primaryKey}}`,
    partialUpdate: `{${this.primaryKey}}`,
    delete: `{${this.primaryKey}}`,
  };

  /**
   * The injection variables to use when formatting the endpoint
   */
  protected injects: UrlInjectionMap = {};

  /**
   * The host to use for accessing the api
   */
  protected host?: string;

  /**
   * The Axios config options to use when making requests
   */
  protected config?: AxiosRequestConfig;

  /**
   * Default axios behaviours to use when making requests
   */
  public static defaultAxiosConfig: AxiosRequestConfig = {
    baseURL: getBaseUrl(),
    // if the server is running on a different domain than the csrftoken cookie is set for this will not work
    // and the CSRF token will need to be retrieved first at /api/return-csrf-cookie/ (non-production environments only)
    withCredentials: true,
    xsrfCookieName: "csrftoken",
    xsrfHeaderName: "X-CSRFToken",
    // Add mobile-specific User-Agent header to identify requests from the mobile app
    ...(getPlatform() === "MOBILE" && {
      headers: { "User-Agent": "Showpass Mobile App" },
    }),
  };

  /**
   * The Axios client instance with which we will query the API
   */
  protected axiosClient?: AxiosInstance;

  /**
   * A getter to provide access to our private client.
   */
  public get client(): AxiosInstance {
    if (!this.axiosClient) {
      this.axiosClient = axios.create(this.config);
    }

    return this.axiosClient;
  }

  public setClient(client: AxiosInstance): this {
    this.axiosClient = client;

    return this;
  }

  /**
   * Initialize a new repository instance and assign our axios defaults
   */
  constructor(injects?: UrlInjectionMap, config?: AxiosRequestConfig) {
    this.setConfig(config ?? {});
    this.setInjects(injects);
    this.responseHandler = new ResponseHandler();
  }

  /**
   * Builds a URL with the given values to be used in our request.
   * @param key The key of the route to build
   * @param injects The values to inject into the URL
   * @param shouldValidateUrl Whether to validate the URL for missing route paraters.
   */
  public buildUrl(
    key?: keyof UrlRouteMap,
    injects: UrlInjectionMap = {},
    shouldValidateUrl = true
  ): string {
    const paths = [this.endpoint];

    if (key) {
      paths.push(this.urlMap[key]);
    }

    const host = this.getHost();
    const path = Url.formatEndpointUrl(
      paths,
      {
        ...this.injects,
        ...injects,
      },
      // used to prevent logging errors when react-query hook uses its enabled param to disable API calls (see jsdoc comment)
      shouldValidateUrl
    );

    if (host) {
      return new URL(path, host).toString();
    }

    return path;
  }

  /**
   * Get the repo's name
   */
  public getRepositoryName(): string {
    return this.repositoryName;
  }

  /**
   * Get the repo's host
   */
  public getHost(): string | undefined {
    return this.host;
  }

  /**
   * Set the host for requests.
   */
  public setHost(host: string): this {
    this.host = new URL(host).origin;

    return this;
  }

  /**
   * Get the config settings to apply to axios requests
   */
  public getConfig(): AxiosRequestConfig {
    return {
      ...this.config,
      ...this.client.defaults,
    };
  }

  /**
   * Set the config settings to apply to axios requests
   */
  public setConfig(config: AxiosRequestConfig): this {
    this.config = {
      ...(this.constructor as typeof Repository).defaultAxiosConfig,
      ...config,
    };

    return this;
  }

  /**
   * Get the injectable variables
   */
  public getInjects(): UrlInjectionMap {
    return this.injects;
  }

  /**
   * Set the injectable variables
   */
  public setInjects(injects: UrlInjectionMap = {}): this {
    this.injects = injects;

    return this;
  }

  /**
   * Set the cookie to use for this repo
   *
   * @deprecated do not use this method, it doesnt work.
   */
  public cookie(cookie?: string): this {
    this.setConfig({
      ...this.getConfig(),
      headers: { cookie },
    });

    return this;
  }

  /**
   * Set the jwt for the repo
   */
  public jwt(token?: string): this {
    this.setConfig({
      ...this.getConfig(),
      headers: { Authorization: token ? `JWT ${token}` : undefined },
    });

    return this;
  }

  /**
   * Semantic method for invalidating our client
   */
  protected invalidateClient(): void {
    this.axiosClient = undefined;
  }

  /**
   * Proxies the class to prefetch all requests made to the API
   * using react-query's prefetchQuery method.
   */
  public prefetch(queryClient: QueryClient, injects?: UrlInjectionMap): this {
    return new Proxy(this, {
      get(target, prop: keyof RepositoryContract) {
        // Allow access to non-urlMap properties
        if (!Object.keys(target.urlMap).includes(String(prop))) {
          return target[prop];
        }

        // Proxy methods that make requests and prefetch them instead
        return new Proxy(target[prop], {
          apply(method, instance, args) {
            return queryClient.prefetchQuery(
              target.buildUrl(prop as keyof UrlRouteMap, injects),
              () => Reflect.apply(method, instance, args)
            );
          },
        });
      },
    });
  }

  /**
   * This is the central method for making HTTP requests to the API, abstracting the Axios request mechanism for
   * ease of customization and configuration. It handles both successful responses and errors gracefully, providing
   * a consistent user experience. Success responses can optionally trigger toast notifications. Errors are processed to
   * display user-friendly messages, also optionally via toast notifications.
   * This method underpins other repository methods such as list, create, retrieve, etc., for their API calls.
   *
   * @param requestUrl The URL endpoint for the API request, expected to be a fully qualified URL.
   * @param config Optional configuration options for the request, extending AxiosRequestConfig. This includes:
   *        - showToastOnSuccess: Determines whether a success notification should be displayed.
            - showToastOnError: Determines whether an error notification should be displayed.
   * @returns A promise that resolves with the AxiosResponse object containing the response data, indicative of a
   *          successful API call.
   * @throws Propagates any encountered error. This allows the calling code to handle the error further,
   *         potentially for additional UI feedback or logging purposes.
   */
  public async request<Response = T>(
    requestUrl: string,
    config?: ExtendedAxiosRequestConfig
  ): Promise<AxiosResponse<Response>> {
    try {
      // Perform the API request
      const response = await HttpRequestFactory.request<Response>(this.client, {
        url: requestUrl,
        ...config,
      });

      // Show success toast if enabled
      this.responseHandler.processSuccess(response, config?.showToastOnSuccess);

      // Return the response
      return response;
    } catch (error) {
      // Handle and show error if enabled
      this.responseHandler.processFailure(
        error,
        config?.showToastOnError === undefined ? true : config.showToastOnError
      );

      // Propagate error for further handling
      throw error;
    }
  }

  /**
   * List all models of an endpoint
   */
  public async list(config: ExtendedAxiosRequestConfig = {}): Promise<T[]> {
    const { data } = await this.request<T[]>(this.buildUrl("list"), {
      method: "get",
      ...config,
    });

    return data;
  }

  /**
   * List all models as a paginator
   */
  public async paginate(config: ExtendedAxiosRequestConfig = {}) {
    const { data } = await this.request<PaginatorContract<T>>(
      this.buildUrl("paginate"),
      {
        method: "get",
        params: {
          page: config?.params?.page ?? 1,
          page_size: config?.params?.page_size ?? 25,
        },
        ...config,
      }
    );

    return data;
  }

  /**
   * Retrieve a specific model
   */
  public async retrieve(
    id?: Identifier,
    config: ExtendedAxiosRequestConfig = {}
  ) {
    const injects: UrlInjectionMap = {
      [this.primaryKey]: id ?? this.injects[this.primaryKey],
    };

    const { data } = await this.request<T>(this.buildUrl("retrieve", injects), {
      method: "get",
      ...config,
    });

    return data;
  }

  /**
   * Create a model
   */
  public async create(
    attributes?: T | Partial<T>,
    config: ExtendedAxiosRequestConfig = {}
  ) {
    const { data } = await this.request<T>(this.buildUrl("create"), {
      method: "post",
      data: attributes,
      ...config,
    });

    return data;
  }

  /**
   * Update a given model
   */
  public async update(
    id?: Identifier,
    attributes?: T | Partial<T>,
    config: ExtendedAxiosRequestConfig = {}
  ) {
    const injects: UrlInjectionMap = {
      [this.primaryKey]: id ?? this.injects[this.primaryKey],
    };

    const { data } = await this.request<T>(this.buildUrl("update", injects), {
      method: "put",
      data: attributes,
      ...config,
    });

    return data;
  }

  /**
   * Partially update a given model
   */
  public async partialUpdate(
    id?: Identifier,
    attributes?: T | Partial<T>,
    config: ExtendedAxiosRequestConfig = {}
  ) {
    const injects: UrlInjectionMap = {
      [this.primaryKey]: id ?? this.injects[this.primaryKey],
    };

    const { data } = await this.request<T>(
      this.buildUrl("partialUpdate", injects),
      {
        method: "patch",
        data: attributes,
        ...config,
      }
    );

    return data;
  }

  /**
   * Delete a provided model
   */
  public async destroy(
    id?: Identifier,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<void> {
    const injects: UrlInjectionMap = {
      [this.primaryKey]: id ?? this.injects[this.primaryKey],
    };

    await this.request<Response>(this.buildUrl("delete", injects), {
      method: "delete",
      ...config,
    });
  }
}

export interface RepositoryContract<T = unknown> {
  client: AxiosInstance;
  getRepositoryName(): string;
  getInjects(): UrlInjectionMap;
  setInjects(injects: UrlInjectionMap): this;
  getConfig(): ExtendedAxiosRequestConfig;
  setClient(client: AxiosInstance): this;
  buildUrl(
    key: keyof UrlRouteMap,
    injects?: UrlInjectionMap,
    queryParams?: ExtendedAxiosRequestConfig["params"]
  ): string;
  jwt(token?: string): this;

  request(
    requestUrl: string,
    config?: ExtendedAxiosRequestConfig
  ): Promise<AxiosResponse<T>>;
  list(config?: ExtendedAxiosRequestConfig): Promise<T[]>;
  paginate(config?: ExtendedAxiosRequestConfig): Promise<PaginatorContract<T>>;
  retrieve(id?: Identifier, config?: ExtendedAxiosRequestConfig): Promise<T>;
  create(
    attributes?: T | Partial<T>,
    config?: ExtendedAxiosRequestConfig
  ): Promise<T>;
  update(
    id?: Identifier,
    attributes?: T | Partial<T>,
    config?: ExtendedAxiosRequestConfig
  ): Promise<T>;
  partialUpdate(
    id?: Identifier,
    attributes?: T | Partial<T>,
    config?: ExtendedAxiosRequestConfig
  ): Promise<T>;
  destroy(id?: Identifier, config?: ExtendedAxiosRequestConfig): Promise<void>;
}

/**
 * Extends AxiosRequestConfig to include an optional flag for controlling toast notifications
 */
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  showToastOnError?: boolean; // Optional property to show toast on error
  showToastOnSuccess?: boolean; // Optional property to show toast on success
}

/**
 * Constructs a type by extracting the generic type parameter `U` from a `Repository<T>` type.
 *
 * This utility is useful for extracting the underlying type managed by a `Repository` wrapper.
 *
 */
export type ExtractSerializer<T> = T extends Repository<infer U> ? U : never;
